import { useState } from "react"
import { Skeleton } from "antd"
import { UserDeleteOutlined, DownOutlined } from "@ant-design/icons"
import { ReadCV } from "src/assets/icons"
import { capitalizeFirstLetter, getDefaultFormattedDate } from "src/utils/helperFunctions"
import { formCategoryWiseInputs } from "./constants"

export const normalizeCandidateFormData = (userData = {}) => ({
  ...userData,
  locationPreference: userData.locationPreference
    ? userData.locationPreference?.split(",")?.join(", ")
    : userData.locationPreference,
})

const hasCategoryData = (category, formData) => {
  if (!formData) return false

  for (const input in formCategoryWiseInputs[category]) {
    if (typeof (formData[input]) === "string" || typeof (formData[input]) === "number") {
      return true
    }
  }

  return false
}

export const hasCandidateFormData = (formData) => (
  Object.keys(formCategoryWiseInputs).some((category) => hasCategoryData(category, formData))
)

const getDisplayValue = ({ formData, input, type, label, reportView }) => {
  if (type === "file") {
    return typeof (formData?.[input]) === "string" && formData?.[input]?.length > 0
      ? (
        <div className="flex items-center">
          {!reportView && (
            <span className="flex items-center justify-center download-icon mr-12">
              <ReadCV />
            </span>
          )}
          <a
            href={formData?.[input]}
            target="_blank"
            rel="noreferrer"
            className="text-14 weight-600 cv-link cursor-pointer"
          >
            View Resume
          </a>
        </div>
      )
      : ""
  }

  if (type === "date") {
    return getDefaultFormattedDate(formData?.[input], "MMM D, YYYY", "DD-MM-YYYY")
  }

  if (type === "year") {
    return getDefaultFormattedDate(formData?.[input], "YYYY")
  }

  if (label === "Gender") {
    return capitalizeFirstLetter(formData?.[input] ?? "")
  }

  if (label === "Notice Period (in days)") {
    return `${formData?.[input]}`
  }

  return formData?.[input] ?? ""
}

const CandidateDetailsSection = ({
  formData,
  loading = false,
  title,
  showTitle = true,
  hideWhenEmpty = false,
  reportView = false,
  collapsible = false,
  defaultCollapsed = false,
  hideCategoryHeaders = reportView,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const categories = Object.keys(formCategoryWiseInputs).filter((category) => hasCategoryData(category, formData))
  const hasData = hasCandidateFormData(formData)

  if (hideWhenEmpty && !loading && !hasData) return null

  const isCollapsed = collapsible && collapsed
  const displayTitle = title || (reportView ? "Additional Candidate Data" : "Candidate Details")

  return (
    <section className={`candidate-details-section ${reportView ? "report-view" : ""} ${collapsible ? "collapsible" : ""} ${isCollapsed ? "collapsed" : ""}`}>
      {showTitle && (
        collapsible ? (
          <button
            type="button"
            className="candidate-details-toggle flex items-center justify-between w-full"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
          >
            <span className={`${reportView ? "candidate-details-title" : "text-50 semibold align-left"}`}>
              {displayTitle}
            </span>
            <DownOutlined className={`collapse-caret ${collapsed ? "collapsed" : ""}`} />
          </button>
        ) : (
          <p className={`${reportView ? "candidate-details-title" : "text-50 semibold mb-30 align-left"}`}>
            {displayTitle}
          </p>
        )
      )}
      {!isCollapsed && (
      <div className="candidate-details-content">
        {loading ? (
          <div className="loading-container grid grid-cols-2 grid-gap-8">
            {Array.from({ length: 4 }, () => null).map((cell, index) => <Skeleton key={index} active />)}
          </div>
        ) : !hasData ? (
          <div className="empty-data-container flex flex-col items-center justify-center">
            <UserDeleteOutlined />
            <div className="oval-shadow mt-1" />
            <p className="text-20 weight-400 letter-spacing-8 mt-10">No details found</p>
          </div>
        ) : (
          categories.map((category, index) => (
            <div
              key={category}
              className={`flex flex-col 
                ${index < (categories.length - 1) ? (hideCategoryHeaders ? "mb-16" : "mb-30") : ""} 
                ${formData ? "slide-up" : ""}`}
            >
              {!hideCategoryHeaders && (
                <p className="category text-14 weight-600 letter-spacing-10 color-primary mb-16 text-uppercase">
                  {category}
                </p>
              )}
              <div className="grid grid-cols-3 grid-gap-25">
                {Object.keys(formCategoryWiseInputs[category]).map((input) => {
                  if (formData[input] === undefined) return null

                  const type = formCategoryWiseInputs[category][input].type
                  const label = type === "file" ? "" : formCategoryWiseInputs[category][input].label
                  const newRow = label === "12th Percentage" || label === "10th Percentage"
                  const value = getDisplayValue({ formData, input, type, label, reportView })

                  return (
                    <div
                      key={input}
                      className={`flex flex-col items-start candidate-detail-field ${newRow ? "grid-new-col" : ""}`}
                    >
                      <p className={`text-14 weight-400 input-label ${reportView ? "" : "opacity-50"}`}>{label}</p>
                      <div className="weight-600 text-16 w-full candidate-detail-value">
                        {!!value ? value : "Not available"}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))
        )}
      </div>
      )}
    </section>
  )
}

export default CandidateDetailsSection
